import React, { useState, useEffect } from 'react';
import apiClient from '../../apiClient';
import { Doughnut } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  ArcElement
} from 'chart.js';
import './PlanAnalyticsModal.css';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  ArcElement
);

function PlanAnalyticsModal({ plan, onClose }) {
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const fetchAnalytics = async () => {
      try {
        const response = await apiClient.get(`/admin/subscription-plans/${plan.id}/analytics`);
        setAnalytics(response.data);
        setLoading(false);
      } catch (err) {
        setError('Failed to fetch analytics');
        setLoading(false);
      }
    };
    
    fetchAnalytics();
  }, [plan.id]);


  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  const getChurnRateColor = (rate) => {
    if (rate < 5) return '#10b981';
    if (rate < 10) return '#f59e0b';
    return '#ef4444';
  };

  if (loading) {
    return (
      <div className="modal-overlay">
        <div className="modal-content analytics-modal">
          <div className="loading">Loading analytics...</div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="modal-overlay">
        <div className="modal-content analytics-modal">
          <div className="error">{error}</div>
        </div>
      </div>
    );
  }

  const subscriptionStatusData = {
    labels: ['Active', 'Canceled', 'Other'],
    datasets: [{
      data: [
        analytics.active_subscriptions,
        analytics.canceled_subscriptions,
        analytics.total_subscriptions - analytics.active_subscriptions - analytics.canceled_subscriptions
      ],
      backgroundColor: ['#10b981', '#ef4444', '#6b7280'],
      borderWidth: 0
    }]
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'bottom'
      }
    }
  };

  return (
    <div className="modal-overlay">
      <div className="modal-content analytics-modal">
        <div className="modal-header">
          <h2>{plan.name} - Analytics</h2>
          <button className="close-btn" onClick={onClose}>×</button>
        </div>

        <div className="analytics-content">
          <div className="metrics-grid">
            <div className="metric-card">
              <h3>Active Subscriptions</h3>
              <div className="metric-value">{analytics.active_subscriptions}</div>
              <div className="metric-label">Currently active</div>
            </div>

            <div className="metric-card">
              <h3>Total Revenue</h3>
              <div className="metric-value">{formatCurrency(analytics.total_revenue)}</div>
              <div className="metric-label">All time</div>
            </div>

            <div className="metric-card">
              <h3>Monthly Recurring Revenue</h3>
              <div className="metric-value">{formatCurrency(analytics.monthly_recurring_revenue)}</div>
              <div className="metric-label">MRR</div>
            </div>

            <div className="metric-card">
              <h3>Churn Rate</h3>
              <div 
                className="metric-value"
                style={{ color: getChurnRateColor(analytics.churn_rate) }}
              >
                {analytics.churn_rate}%
              </div>
              <div className="metric-label">Last 30 days</div>
            </div>
          </div>

          <div className="chart-section">
            <h3>Subscription Distribution</h3>
            <div className="chart-container">
              <Doughnut data={subscriptionStatusData} options={chartOptions} />
            </div>
          </div>

          <div className="details-section">
            <h3>Plan Details</h3>
            <div className="detail-row">
              <span className="detail-label">Total Subscriptions:</span>
              <span className="detail-value">{analytics.total_subscriptions}</span>
            </div>
            <div className="detail-row">
              <span className="detail-label">Canceled Subscriptions:</span>
              <span className="detail-value">{analytics.canceled_subscriptions}</span>
            </div>
            <div className="detail-row">
              <span className="detail-label">Average Revenue per User:</span>
              <span className="detail-value">
                {analytics.active_subscriptions > 0 
                  ? formatCurrency(analytics.monthly_recurring_revenue / analytics.active_subscriptions)
                  : '$0.00'
                }
              </span>
            </div>
          </div>
        </div>

        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={onClose}>
            Close
          </button>
        </div>
      </div>
    </div>
  );
}

export default PlanAnalyticsModal;